import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useNavigation } from 'react-router';
import { useDeferredLoading } from '@/hooks/useLoadingUx';
import { cn } from '@/lib/utils';

/**
 * The one global "something is happening" signal.
 *
 * It watches three things at once — the router fetching a code-split route,
 * react-query fetching data, and any in-flight mutation — so no screen has to
 * wire up its own top-level indicator.
 *
 * Two properties matter more than the visuals:
 *
 *  - **It cannot flash.** {@link useDeferredLoading} swallows anything under
 *    ~200ms and holds anything it does show, which is what stops the bar
 *    strobing on a warm cache.
 *  - **It cannot cost layout.** The bar is `position: fixed` over the chrome and
 *    animates `transform` only, so it never reflows the page beneath it and
 *    never competes with the render it is reporting on.
 */
export function RouteProgress({ className }: { className?: string }) {
  const navigation = useNavigation();
  const fetching = useIsFetching();
  const mutating = useIsMutating();

  const busy = navigation.state !== 'idle' || fetching > 0 || mutating > 0;
  const visible = useDeferredLoading(busy, { delay: 200, minDuration: 500 });

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 top-0 z-50 h-[3px] overflow-hidden transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0',
        className,
      )}
      role="progressbar"
      aria-hidden={!visible}
      aria-label="Loading"
    >
      {visible && (
        <div className="animate-progress h-full w-full origin-left bg-gradient-to-r from-brand-400 via-brand-600 to-dispatch-500" />
      )}
    </div>
  );
}
